import React, { useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { RootState, AppDispatch } from '../../../src/redux/store';
import {
  setDietaryRestrictions,
  setOtherDietaryNotes,
  addDislikedIngredient,
  removeDislikedIngredient,
  setMealFrequencyPreferences,
  MealFrequencyPreferences,
} from '../../../src/redux/userPreferencesSlice';
import { parseDietaryPreferences } from '../../../src/services/dietParserService';
import { DietType } from '../../../src/types/recipe';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../../src/styles/theme';

interface DietOption {
  type: DietType;
  icon: keyof typeof Feather.glyphMap;
  labelKey: string;
  descriptionKey: string;
}

const DIET_OPTIONS: DietOption[] = [
  { type: 'vegetarian', icon: 'feather', labelKey: 'vegetarian', descriptionKey: 'vegetarianDesc' },
  { type: 'vegan', icon: 'sun', labelKey: 'vegan', descriptionKey: 'veganDesc' },
  { type: 'gluten-free', icon: 'wind', labelKey: 'glutenFree', descriptionKey: 'glutenFreeDesc' },
  { type: 'dairy-free', icon: 'droplet', labelKey: 'dairyFree', descriptionKey: 'dairyFreeDesc' },
];

export default function DietPreferencesEditScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const savedDiets = useSelector((state: RootState) => state.userPreferences?.dietaryRestrictions ?? []);
  const savedIngredients = useSelector((state: RootState) => state.userPreferences?.dislikedIngredients ?? []);
  const savedNotes = useSelector((state: RootState) => state.userPreferences?.otherDietaryNotes ?? '');
  const savedFrequency = useSelector((state: RootState) => state.userPreferences?.mealFrequencyPreferences ?? {});

  const originalIngredients = useRef(savedIngredients);

  const [selectedDiets, setSelectedDiets] = useState<DietType[]>(savedDiets);
  const [localIngredients, setLocalIngredients] = useState<string[]>(savedIngredients);
  const [localFrequency, setLocalFrequency] = useState<MealFrequencyPreferences>(savedFrequency);
  const [freeText, setFreeText] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parsedIngredients, setParsedIngredients] = useState<string[] | null>(null);
  const [parsedFrequency, setParsedFrequency] = useState<MealFrequencyPreferences | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const toggleDiet = useCallback((type: DietType) => {
    setSelectedDiets(prev =>
      prev.includes(type) ? prev.filter(d => d !== type) : [...prev, type],
    );
  }, []);

  const removeExistingChip = useCallback((ingredient: string) => {
    setLocalIngredients(prev => prev.filter(i => i !== ingredient));
  }, []);

  const removeLocalFreqKey = useCallback((key: keyof MealFrequencyPreferences) => {
    setLocalFrequency(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleParseText = useCallback(async () => {
    if (!freeText.trim()) return;
    setIsParsing(true);
    try {
      const results = await parseDietaryPreferences(freeText);
      const dedupedIngredients = results.ingredientExclusions.filter(r => !localIngredients.includes(r));
      setParsedIngredients(dedupedIngredients);
      setParsedFrequency(
        results.mealFrequency.vegetarianPerWeek !== undefined ||
        results.mealFrequency.fishPerWeek !== undefined
          ? results.mealFrequency
          : null,
      );
    } finally {
      setIsParsing(false);
    }
  }, [freeText, localIngredients]);

  const removeParsedTag = useCallback((ingredient: string) => {
    setParsedIngredients(prev => prev?.filter(i => i !== ingredient) ?? null);
  }, []);

  const removeParsedFreqKey = useCallback((key: keyof MealFrequencyPreferences) => {
    setParsedFrequency(prev => {
      if (!prev) return null;
      const next = { ...prev };
      delete next[key];
      return Object.keys(next).length > 0 ? next : null;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      dispatch(setDietaryRestrictions(selectedDiets));

      const removedIngredients = originalIngredients.current.filter(
        i => !localIngredients.includes(i),
      );
      for (const ingredient of removedIngredients) {
        dispatch(removeDislikedIngredient(ingredient));
      }

      const confirmedNew = parsedIngredients ?? [];
      for (const ingredient of confirmedNew) {
        dispatch(addDislikedIngredient(ingredient));
      }

      // Merge parsed frequency into local frequency
      const mergedFrequency: MealFrequencyPreferences = { ...localFrequency };
      if (parsedFrequency?.vegetarianPerWeek !== undefined) {
        mergedFrequency.vegetarianPerWeek = parsedFrequency.vegetarianPerWeek;
      }
      if (parsedFrequency?.fishPerWeek !== undefined) {
        mergedFrequency.fishPerWeek = parsedFrequency.fishPerWeek;
      }
      dispatch(setMealFrequencyPreferences(mergedFrequency));

      const appendedNotes =
        freeText.trim()
          ? savedNotes
            ? `${savedNotes}\n${freeText.trim()}`
            : freeText.trim()
          : savedNotes;
      dispatch(setOtherDietaryNotes(appendedNotes));

      router.back();
    } finally {
      setIsSaving(false);
    }
  }, [
    selectedDiets,
    localIngredients,
    localFrequency,
    parsedIngredients,
    parsedFrequency,
    freeText,
    savedNotes,
    dispatch,
    router,
  ]);

  const hasLocalFrequency = localFrequency.vegetarianPerWeek !== undefined || localFrequency.fishPerWeek !== undefined;
  const hasParsedContent =
    (parsedIngredients !== null && parsedIngredients.length > 0) ||
    (parsedFrequency !== null && (parsedFrequency.vegetarianPerWeek !== undefined || parsedFrequency.fishPerWeek !== undefined));

  return (
    <View style={styles.container}>
      <View style={styles.navHeader}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Feather name="arrow-left" size={22} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.navTitle}>{t('diet.navTitle')}</Text>
        <View style={styles.navSpacer} />
      </View>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.sectionLabel}>{t('diet.dietType')}</Text>
        <View style={styles.optionsContainer}>
          {DIET_OPTIONS.map(option => {
            const isSelected = selectedDiets.includes(option.type);
            return (
              <TouchableOpacity
                key={option.type}
                style={[styles.optionCard, isSelected && styles.optionCardSelected]}
                onPress={() => toggleDiet(option.type)}
                activeOpacity={0.7}
              >
                <View style={[styles.iconContainer, isSelected && styles.iconContainerSelected]}>
                  <Feather
                    name={option.icon}
                    size={22}
                    color={isSelected ? Paprika.background : Paprika.textSecondary}
                  />
                </View>
                <View style={styles.optionText}>
                  <Text style={[styles.optionLabel, isSelected && styles.optionLabelSelected]}>
                    {t(`diet.${option.labelKey}`)}
                  </Text>
                  <Text style={styles.optionDescription}>{t(`diet.${option.descriptionKey}`)}</Text>
                </View>
                <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
                  {isSelected && <Feather name="check" size={13} color={Paprika.background} />}
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/*
          Only vegetarian and vegan actually filter anything today. Saying so
          beats letting a ticked box imply a promise nothing keeps.
        */}
        <View style={styles.coverageNote}>
          <Feather name="info" size={14} color={Paprika.textSecondary} style={styles.coverageIcon} />
          <Text style={styles.coverageText}>{t('diet.filterCoverage')}</Text>
        </View>

        <Text style={styles.sectionLabel}>{t('diet.excludedIngredients')}</Text>
        {localIngredients.length > 0 ? (
          <View style={styles.chipsRow}>
            {localIngredients.map(ingredient => (
              <TouchableOpacity
                key={ingredient}
                style={styles.chip}
                onPress={() => removeExistingChip(ingredient)}
                activeOpacity={0.7}
              >
                <Text style={styles.chipText}>{ingredient}</Text>
                <Feather name="x" size={12} color={Paprika.textSecondary} />
              </TouchableOpacity>
            ))}
          </View>
        ) : (
          <Text style={styles.emptyNote}>{t('diet.noExcluded')}</Text>
        )}

        <Text style={[styles.sectionLabel, styles.sectionLabelSpaced]}>{t('diet.mealFrequency')}</Text>
        {hasLocalFrequency ? (
          <View style={styles.chipsRow}>
            {localFrequency.vegetarianPerWeek !== undefined && (
              <TouchableOpacity
                style={[styles.chip, styles.freqChip]}
                onPress={() => removeLocalFreqKey('vegetarianPerWeek')}
                activeOpacity={0.7}
              >
                <Feather name="feather" size={11} color={Paprika.textSecondary} />
                <Text style={styles.chipText}>{t('diet.vegetarianPerWeek', { count: localFrequency.vegetarianPerWeek })}</Text>
                <Feather name="x" size={12} color={Paprika.textSecondary} />
              </TouchableOpacity>
            )}
            {localFrequency.fishPerWeek !== undefined && (
              <TouchableOpacity
                style={[styles.chip, styles.freqChip]}
                onPress={() => removeLocalFreqKey('fishPerWeek')}
                activeOpacity={0.7}
              >
                <Feather name="anchor" size={11} color={Paprika.textSecondary} />
                <Text style={styles.chipText}>{t('diet.fishPerWeek', { count: localFrequency.fishPerWeek })}</Text>
                <Feather name="x" size={12} color={Paprika.textSecondary} />
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <Text style={styles.emptyNote}>{t('diet.noFrequency')}</Text>
        )}

        {savedNotes.length > 0 && (
          <Text style={styles.notesReference}>{savedNotes}</Text>
        )}

        <Text style={[styles.sectionLabel, styles.sectionLabelSpaced]}>{t('diet.addMore')}</Text>
        <Text style={styles.freeTextHint}>{t('diet.addMoreHint')}</Text>
        <View style={styles.inputRow}>
          <TextInput
            style={styles.textInput}
            value={freeText}
            onChangeText={text => {
              setFreeText(text);
              setParsedIngredients(null);
              setParsedFrequency(null);
            }}
            placeholder={t('diet.addMorePlaceholder')}
            placeholderTextColor={Paprika.textMuted}
            multiline
            numberOfLines={2}
            returnKeyType="done"
          />
          {freeText.trim().length > 0 && parsedIngredients === null && parsedFrequency === null && (
            <TouchableOpacity
              style={styles.parseButton}
              onPress={handleParseText}
              disabled={isParsing}
              activeOpacity={0.7}
            >
              {isParsing ? (
                <ActivityIndicator size="small" color={Paprika.background} />
              ) : (
                <Text style={styles.parseButtonText}>{t('diet.parse')}</Text>
              )}
            </TouchableOpacity>
          )}
        </View>

        {hasParsedContent && (
          <View style={styles.tagsSection}>
            <Text style={styles.tagsLabel}>{t('diet.understood')}</Text>
            <View style={styles.chipsRow}>
              {(parsedIngredients ?? []).map(ingredient => (
                <TouchableOpacity
                  key={ingredient}
                  style={styles.chip}
                  onPress={() => removeParsedTag(ingredient)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.chipText}>{ingredient}</Text>
                  <Feather name="x" size={12} color={Paprika.textSecondary} />
                </TouchableOpacity>
              ))}
              {parsedFrequency?.vegetarianPerWeek !== undefined && (
                <TouchableOpacity
                  style={[styles.chip, styles.freqChip]}
                  onPress={() => removeParsedFreqKey('vegetarianPerWeek')}
                  activeOpacity={0.7}
                >
                  <Feather name="feather" size={11} color={Paprika.textSecondary} />
                  <Text style={styles.chipText}>{t('diet.vegetarianPerWeek', { count: parsedFrequency.vegetarianPerWeek })}</Text>
                  <Feather name="x" size={12} color={Paprika.textSecondary} />
                </TouchableOpacity>
              )}
              {parsedFrequency?.fishPerWeek !== undefined && (
                <TouchableOpacity
                  style={[styles.chip, styles.freqChip]}
                  onPress={() => removeParsedFreqKey('fishPerWeek')}
                  activeOpacity={0.7}
                >
                  <Feather name="anchor" size={11} color={Paprika.textSecondary} />
                  <Text style={styles.chipText}>{t('diet.fishPerWeek', { count: parsedFrequency.fishPerWeek })}</Text>
                  <Feather name="x" size={12} color={Paprika.textSecondary} />
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}

        {parsedIngredients !== null && parsedFrequency === null && parsedIngredients.length === 0 && freeText.trim().length > 0 && (
          <Text style={styles.noIngredientsNote}>{t('diet.nothingDetected')}</Text>
        )}
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={isSaving}
          activeOpacity={0.8}
        >
          {isSaving ? (
            <ActivityIndicator color={Paprika.background} />
          ) : (
            <Text style={styles.saveButtonText}>{t('diet.save')}</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },

  navHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: Platform.OS === 'ios' ? 56 : PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
    backgroundColor: Paprika.background,
  },
  backButton: {
    width: 36,
    alignItems: 'flex-start',
  },
  navTitle: {
    flex: 1,
    textAlign: 'center',
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  navSpacer: {
    width: 36,
  },

  scrollContent: {
    padding: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.xxl,
  },

  sectionLabel: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
  },
  sectionLabelSpaced: {
    marginTop: PaprikaSpacing.xl,
  },

  optionsContainer: {
    gap: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.xl,
  },

  optionCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.lg,
    borderWidth: 1,
    borderColor: Paprika.border,
    backgroundColor: Paprika.background,
    gap: PaprikaSpacing.md,
  },
  optionCardSelected: {
    borderColor: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLight,
  },

  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: Paprika.surfaceLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainerSelected: {
    backgroundColor: Paprika.textPrimary,
  },

  optionText: {
    flex: 1,
    gap: 2,
  },
  optionLabel: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  optionLabelSelected: {
    fontWeight: '600',
  },
  optionDescription: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: Paprika.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: Paprika.textPrimary,
    borderColor: Paprika.textPrimary,
  },

  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.xs,
    marginBottom: PaprikaSpacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: Paprika.surfaceLight,
    borderRadius: PaprikaRadius.sm,
    paddingHorizontal: PaprikaSpacing.sm,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  freqChip: {
    borderColor: Paprika.accent,
  },
  chipText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textPrimary,
  },

  coverageNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.lg,
  },
  coverageIcon: {
    marginTop: 2,
  },
  coverageText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    flex: 1,
  },
  emptyNote: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    marginBottom: PaprikaSpacing.sm,
  },

  notesReference: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    fontStyle: 'italic',
    marginTop: PaprikaSpacing.xs,
  },

  freeTextHint: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.sm,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: PaprikaSpacing.sm,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
    minHeight: 56,
    ...Platform.select({ web: { outlineStyle: 'none' } as any }),
  },
  parseButton: {
    backgroundColor: Paprika.textPrimary,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 64,
    minHeight: 56,
  },
  parseButtonText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.background,
  },

  tagsSection: {
    gap: PaprikaSpacing.xs,
    marginTop: PaprikaSpacing.sm,
  },
  tagsLabel: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  noIngredientsNote: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    fontStyle: 'italic',
    marginTop: PaprikaSpacing.xs,
  },

  footer: {
    padding: PaprikaSpacing.lg,
    paddingBottom: Platform.OS === 'ios' ? PaprikaSpacing.xl : PaprikaSpacing.lg,
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
    backgroundColor: Paprika.background,
  },
  saveButton: {
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.lg,
    paddingVertical: PaprikaSpacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.background,
  },
});
